/* eslint-disable @typescript-eslint/naming-convention */
import React, { useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, alpha } from '@mui/material';
import { palette, button, chip } from './components';
import { colors } from './colors';

// Update the Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
    green: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    green: true;
    primaryDark: true;
  }
}

interface ThemeHandlerProps {
  children:any;
}

declare module '@mui/material/Switch' {
  interface SwitchPropsColorOverrides {
    primaryDark: true;
  }
}

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    primaryDark: true;
    neutral: true;
  }
}

const ThemeHandler = ({ children }: ThemeHandlerProps) => {
  const theme = useMemo(
    () =>
      createTheme({
        palette,
        shape: {
          borderRadius: 8,
        },
        typography: {
          fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        components: {
          MuiButton: button,
          MuiChip: chip,
          MuiBackdrop: {
            styleOverrides: {
              root: {
                backgroundColor: alpha('#243D42', 0.5),
              },
              invisible: {
                background: 'none',
              },
            },
          },
          MuiPaper: {
            defaultProps: {
              variant: 'outlined',
            },
            styleOverrides: {
              root: {
                borderRadius: 16,
                borderColor: '#D8D8D8',
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              fullWidth: true,
              size: 'small',
            },
          },
          MuiAlert: {
            defaultProps: {
              variant: 'filled',
            },
            styleOverrides: {
              root: {
                fontSize: 16,
              },
              filled: {
                borderRadius: 4,
                border: 'none',
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.16)',
              },
              filledError: {
                backgroundColor: '#ffefef',
                color: colors.text,
                ':hover': {
                  backgroundColor: '#ffefef',
                },
              },
              filledSuccess: {
                backgroundColor: '#EFFFF9',
                color: colors.text,
                ':hover': {
                  backgroundColor: '#EFFFF9',
                },
              },
              filledWarning: {
                backgroundColor: '#fff5ef',
                color: colors.text,
                ':hover': {
                  backgroundColor: '#fff5ef',
                },
              },
              filledInfo: {
                color: colors.text,
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                marginLeft: 0,
              },
            },
          },
          MuiSkeleton: {
            defaultProps: {
              variant: 'rectangular',
              animation: 'wave',
            },
            styleOverrides: {
              rectangular: {
                borderRadius: 4,
              },
              text: {
                borderRadius: 4,
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              endAdornment: {
                right: 10,
              },
              popper: { zIndex: '999' },
            },
          },
        },
      }),
    []
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default ThemeHandler;
